import { useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "../i18n/useT";
import { useStore, uid } from "../store/store";
import type { Campaign } from "../store/types";
import type { CampaignType, Channel } from "@mensajeando/compliance";
import { SAMPLE_VOTER_FLOW } from "@mensajeando/flows";

export function Campaigns() {
  const t = useT();
  const campaigns = useStore((s) => s.campaigns);
  const addCampaign = useStore((s) => s.addCampaign);
  const upsertFlow = useStore((s) => s.upsertFlow);
  const orgId = useStore((s) => s.organizations[0]?.id) ?? "org_main";
  const clientId = useStore((s) => s.clients[0]?.id) ?? "client_demo";

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{ name: string; type: CampaignType; channel: Channel }>({
    name: "", type: "political", channel: "sms",
  });

  function create() {
    if (!draft.name.trim()) return;
    upsertFlow(SAMPLE_VOTER_FLOW);
    const c: Campaign = {
      id: uid("camp"),
      organizationId: orgId,
      clientId,
      name: draft.name,
      type: draft.type,
      channel: draft.channel,
      language: "auto",
      flowId: SAMPLE_VOTER_FLOW.id,
      senderEligible: false,
      campaignRegistered: false,
      requiresConsent: true,
      createdAt: new Date().toISOString(),
      status: "draft",
      startedConversations: 0,
      completedConversations: 0,
    };
    addCampaign(c);
    setOpen(false);
    setDraft({ name: "", type: "political", channel: "sms" });
  }

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t.nav.campaigns}</h1>
        <button className="btn-primary" onClick={() => setOpen(true)}>+ {t.campaign.new}</button>
      </header>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Channel</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Registration</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="p-3 font-medium">
                  <Link to={`/app/campaigns/${c.id}`} className="text-brand-700 hover:underline">{c.name}</Link>
                </td>
                <td className="p-3">{t.campaign.types[c.type]}</td>
                <td className="p-3">{t.campaign.channels[c.channel]}</td>
                <td className="p-3">
                  <StatusBadge status={c.status} />
                </td>
                <td className="p-3">
                  {c.campaignRegistered && c.senderEligible
                    ? <span className="badge-ok">{t.campaign.registered}</span>
                    : <span className="badge-warn">{t.campaign.notRegistered}</span>}
                </td>
                <td className="p-3 text-right">
                  <Link to={`/app/campaigns/${c.id}`} className="btn-secondary">Open</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal title={t.campaign.new} onClose={() => setOpen(false)}>
          <div className="space-y-3">
            <div>
              <label className="label">Name</label>
              <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <label className="label">{t.campaign.type}</label>
              <select className="input" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as CampaignType })}>
                <option value="political">{t.campaign.types.political}</option>
                <option value="nonprofit">{t.campaign.types.nonprofit}</option>
                <option value="employer">{t.campaign.types.employer}</option>
                <option value="commercial">{t.campaign.types.commercial}</option>
              </select>
            </div>
            <div>
              <label className="label">{t.campaign.channel}</label>
              <select className="input" value={draft.channel} onChange={(e) => setDraft({ ...draft, channel: e.target.value as Channel })}>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
            <p className="text-xs text-slate-500">{t.campaign.bilingualNote}</p>
            <div className="flex gap-2 justify-end">
              <button className="btn-secondary" onClick={() => setOpen(false)}>{t.common.cancel}</button>
              <button className="btn-primary" onClick={create}>{t.common.create}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Campaign["status"] }) {
  const map: Record<Campaign["status"], string> = {
    draft: "badge-muted",
    live: "badge-ok",
    paused: "badge-warn",
    complete: "badge-info",
  };
  return <span className={map[status]}>{status}</span>;
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-slate-900/40 grid place-items-center z-50" onClick={onClose}>
      <div className="card card-pad w-[480px]" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}
